// crewbit-admin/src/components/schedule/ConflictBanner.tsx
//
// Schedule Module Phase C C1 — top-of-canvas conflict banner.
// Spec: docs/references/schedule/SCHEDULE_MODULE_SPEC_v1.md §2.6.3 + §0.12.
//
// Body parallel to mobile's ConflictBanner.tsx. A11y: role="alert", first focus
// on Override button via autoFocus.

import { useEffect, useRef } from 'react';
import { AlertTriangle } from 'lucide-react';
import { palette, urgency } from '../../styles/tokens';
import type { ConflictDetail } from '../../lib/errorMap';

const WARNING_BG = '#FEF3C7';

export interface ConflictBannerProps {
  conflicts: ConflictDetail[];
  pendingMutationContext?: {
    rpcName: string;
    onOverride: () => Promise<void>;
  };
  onDismiss: () => void;
}

function describeConflict(c: ConflictDetail): string {
  if (c.description) return c.description;
  if (c.type === 'double_booking') return 'Double-booking with another entry';
  if (c.type === 'resource_double_book') return 'Resource is already booked';
  if (c.type === 'overtime') {
    const t = c.total_hours, h = c.threshold;
    return `Operator over ${h ?? 40}h${t != null ? ` (${t}h scheduled)` : ''}`;
  }
  if (c.type === 'rest_period') return 'Insufficient rest between shifts';
  return 'Schedule conflict';
}

export function ConflictBanner({ conflicts, pendingMutationContext, onDismiss }: ConflictBannerProps) {
  const overrideRef = useRef<HTMLButtonElement>(null);

  useEffect(() => {
    if (pendingMutationContext && overrideRef.current) {
      overrideRef.current.focus();
    }
  }, [pendingMutationContext]);

  return (
    <div
      role="alert"
      style={{
        backgroundColor: WARNING_BG,
        borderBottom: `1px solid ${urgency.warning}`,
        borderRadius: 8,
        padding: 16,
        margin: 16,
      }}
    >
      <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 4 }}>
        <AlertTriangle size={20} color={urgency.warning} />
        <span style={{ fontSize: 17, fontWeight: 600, color: urgency.warning }}>
          Schedule conflict detected
        </span>
      </div>
      <div style={{ fontSize: 14, color: palette.g700, marginBottom: 12 }}>
        {conflicts.map(describeConflict).join(' · ')}
      </div>
      <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 12 }}>
        <button
          onClick={onDismiss}
          style={{
            padding: '8px 16px', borderRadius: 8,
            background: 'transparent', color: palette.g600,
            border: `1px solid ${palette.g300}`,
            fontSize: 14, fontWeight: 500, cursor: 'pointer',
          }}
        >
          Dismiss
        </button>
        {pendingMutationContext ? (
          <button
            ref={overrideRef}
            onClick={() => { void pendingMutationContext.onOverride(); }}
            style={{
              padding: '8px 20px', borderRadius: 8,
              background: urgency.warning, color: palette.white,
              border: 'none', fontSize: 14, fontWeight: 600, cursor: 'pointer',
            }}
          >
            Override
          </button>
        ) : null}
      </div>
    </div>
  );
}
